import VueI18n from "vue-i18n";

import {
  errorNotificationWrapper,
  warningNotificationWrapper
} from "../../../../../logic/helpers/NotificationService";
import PostApi from '../../../../../plugins/AdminTemplate/includes/logic/Api/PostApi'
import FileApi from '../../../../File/logic/file-api'
import { MediaEditorMode } from "../../../types";
import { CustomMediaTypes } from '../../types'
import { FileType, MediaFileStatus, MessageAttachment } from '../types'
import { BaseMediaFileData, MediaFileData } from './MediaFileData'
import { FilesWithPreviewToUpload } from "./filesToUpload";

export default class MediaManager {
  attachments!: Array<BaseMediaFileData>

  selectedType: FileType | null = null

  selectedCustomSubType: string | null = null

  private API: FileApi

  targetToUpload: Record<string, string | number>

  editorMode!: MediaEditorMode;

  i18n: VueI18n;

  get selectedTypeIsImage() {
    return this.selectedType === FileType.Image || this.selectedType === FileType.SpoilerImage
  }

  get selectedTypeIsVideo() {
    return this.selectedType === FileType.Video || this.selectedType === FileType.SpoilerVideo
  }

  get selectedTypeIsAnimation() {
    return this.selectedType === FileType.Animation || this.selectedType === FileType.SpoilerAnimation
  }

  constructor(
    attachments: Array<MessageAttachment>,
    { target, target_id, }: Record<string, string | number>,
    editorMode: MediaEditorMode,
    i18n: VueI18n,
    customMediaTransformer: CustomMediaTypes['classParser'] | undefined
  ) {
    this.API = new FileApi(PostApi.BASE_URL)

    this.attachments = attachments
      .filter(a => a.file !== null)
      .map((a) => {
        if (customMediaTransformer && a.type === FileType.Custom) {
          return customMediaTransformer(a)
        }

        return new MediaFileData(a)
      })

    this.targetToUpload = { [target]: target_id }
    this.editorMode = editorMode
    this.i18n = i18n
  }

  getAttachment(file: string): MessageAttachment | undefined {
    const attachment = this.attachments.find((a) => a.file === file)
    return attachment?.getModel()
  }

  getAttachmentPreview<T extends BaseMediaFileData>(attachment: T): string {
    if (attachment.file) {
      if (attachment.type === FileType.Image) {
        return this.API.getFilePreviewUrl(attachment.file, this.targetToUpload.target.toString())
      }

      return this.API.getFilePreviewUrl(attachment.file, this.targetToUpload.target.toString())
    }

    return ''
  }

  setFileType(type: FileType | null): void {
    this.selectedType = type
  }

  setFileSubType(type: string): void {
    this.selectedCustomSubType = type
  }

  removeAttachment(attachment: MediaFileData): void {
    if (attachment.abort) {
      attachment.abort()
    }

    this.attachments = this.attachments.filter(a => a.guid !== attachment.guid)

    this.updateModel()
  }

  updateModel() {
    if (this.onUpdateHandler) {
      this.onUpdateHandler(
        this.attachments
          .filter(a => a.file !== null)
          .map((a) => a.getModel())
      )
    }
  }

  uploadLimit() {
    if (this.editorMode === MediaEditorMode.TelegramShort) {
      return 1
    } else {
      return 10
    }
  }

  canUploadMoreFiles() {
    return this.attachments.length < this.uploadLimit()
  }

  loadFilePromise(formData: FormData, fileToUpload: MediaFileData, controller: AbortController) {
    return this.API.uploadFile(formData, controller.signal, (val) => fileToUpload.setProgress(val))
      .then(({ data }) => {
        fileToUpload.setFile(data.file_descriptor)
        fileToUpload.setStatus(MediaFileStatus.success)
      })
      .catch((e) => {
        errorNotificationWrapper(this.i18n.t('error').toString(), e)

        fileToUpload.setProgress(100)
        fileToUpload.setStatus(MediaFileStatus.error)

        this.attachments = this.attachments.filter(a => a.guid !== fileToUpload.guid)
      })
  }

  prepareMessagePreviewUrl(url: string) {
    const currentType = this.selectedType

    if (currentType === FileType.MessagePreview) {
      this.uploadMessagePreviewUrl(url, FileType.MessagePreviewUrl)
    }

    if (currentType === FileType.TopMessagePreview) {
      this.uploadMessagePreviewUrl(url, FileType.TopMessagePreviewUrl)
    }
  }

  private uploadMessagePreviewUrl(url: string, type: FileType.TopMessagePreviewUrl | FileType.MessagePreviewUrl) {
    if (this.canUploadMoreFiles() && this.selectedType) {
      this.toggleOnUploadFiles(true)

      const controller = new AbortController()

      const toUploadFile = new MediaFileData({
        file: url,
        type: this.selectedType
      }, () => controller.abort())

      this.attachments.push(toUploadFile)

      const formData: FormData = new FormData()
      const target = Object.entries(this.targetToUpload)[0]

      formData.append('url', url)
      formData.append(target[0], target[1] as string)
      formData.append('type', type)

      const promise = this.loadFilePromise(formData, toUploadFile, controller)

      promise
        .then(res => this.updateModel())
        .finally(() => {
          this.toggleOnUploadFiles(false)
        })
    }
  }

  private toggleOnUploadFiles(state: boolean) {
    if (this.onUploadFiles) {
      this.onUploadFiles(state)
    }
  }

  // eslint-disable-next-line sonarjs/cognitive-complexity
  upload(files: FilesWithPreviewToUpload, onLimitCallback?: () => void) {
    if (this.selectedType) {
      const filesPromises: Array<Promise<void>> = []

      this.toggleOnUploadFiles(true)

      for (const file of files) {
        if ((this.selectedType === FileType.MessagePreview || this.selectedType === FileType.TopMessagePreview) && (file.file.size > 5_000_000)) {
          warningNotificationWrapper(this.i18n.t('warning').toString(), this.i18n.t('file_upload_preview_max_file_size_warning').toString())

          continue
        }

        if (this.canUploadMoreFiles()) {
          const controller = new AbortController()

          const toUploadFile = new MediaFileData({
            file:file.file,
            type: file?.uploadInfo?.type ?? this.selectedType,
          }, () => controller.abort())

          this.attachments.push(toUploadFile)

          const formData: FormData = new FormData()
          const target = Object.entries(this.targetToUpload)[0]

          formData.append(target[0], target[1] as string)
          if (file.thumbFile) {
            formData.append('thumb', file.thumbFile, file.thumbFile.name)
          }

          formData.append('file', file.file, file.file.name)
          formData.append('type', this.selectedType)

          const promise = this.loadFilePromise(formData, toUploadFile, controller)

          filesPromises.push(promise)
        } else {
          if (typeof onLimitCallback === 'function') {
            onLimitCallback()
          }

          break;
        }
      }

      filesPromises.forEach(p => {
        p.then(() => this.updateModel())
      })

      Promise.all(filesPromises)
        .finally(() => {
          this.toggleOnUploadFiles(false)
        })
    }
  }

  /** Обработчики, назначающиеся извне */
  addOnUploadFilesHandler(callback: (val: boolean) => void): void {
    this.onUploadFiles = callback
  }

  onUploadFiles: null | ((val: boolean) => void) = null

  addOnUpdateHandler(fn: (files: Array<MessageAttachment>) => void): void {
    this.onUpdateHandler = fn
  }

  private onUpdateHandler: null | ((attachments: Array<MessageAttachment>) => void) = null

  /**-----------------------------------*/

  static getFileIcon(type: FileType): string {
    switch (type) {
      case FileType.Image:
      case FileType.SpoilerImage:
        return 'picture'
      case FileType.Video:
      case FileType.SpoilerVideo:
        return 'video-camera'
      case FileType.VideoNote:
        return 'camera'
      case FileType.Animation:
      case FileType.SpoilerAnimation:
        return 'build'
      case FileType.Audio:
        return 'sound'
      case FileType.Voice:
        return 'audio'
      case FileType.Document:
        return 'file'
      case FileType.MessagePreview:
      case FileType.MessagePreviewUrl:
        return 'border-left'
      case FileType.TopMessagePreview:
      case FileType.TopMessagePreviewUrl:
        return 'align-center'
      case FileType.Custom:
        return ''
    }
  }
}
